import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MaskitoDirective } from '@maskito/angular';
import { MaskitoOptions, maskitoTransform } from '@maskito/core';

@Component({
  selector: 'app-sin-input',
  standalone: true,
  imports: [CommonModule, MaskitoDirective],
  templateUrl: './sin-input.component.html',
  styleUrls: ['./sin-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SINInputComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.Default
})
export class SINInputComponent implements ControlValueAccessor {
  @Input() controlId: string | null = null;
  @Input() placeholder: string = '';
  @Input() showHideSINButton: boolean = true;
  @Input() maskWhileTyping: boolean = false;
  @Input() error: boolean = false;

  @Output() valueChange = new EventEmitter<string | null>();
  @ViewChild('hiddenSIN', { static: true }) hiddenSINElement!: ElementRef<HTMLDivElement>;
  @ViewChild('sinInput') sinInputElement?: ElementRef<HTMLInputElement>;

  public showingSIN: boolean = false;

  public showMaskedSIN = false;

  public maskOptions: MaskitoOptions = {
    mask: [/\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/]
  };

  public value: string = '';
  public disabled: boolean = false;

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  public writeValue(value: string | null): void {
    this.value = value === null ? '' : this.getMaskedValue(value);
  }

  public registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.showingSIN = false;
    this.showMaskedSIN = true;
    if (isDisabled) {
      setTimeout(() => {
        this.hiddenSINElement.nativeElement.innerHTML = this.getHiddenSINString();
      });
    }
  }

  public handleInput(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value;
    this.value = inputValue;
    const unmaskedValue = this.getUnmaskedValue(inputValue);
    this.valueChange.emit(unmaskedValue);
    this.onChange(unmaskedValue);
  }

  public handleBlur(): void {
    this.onTouched();
    const unmaskedValue = this.getUnmaskedValue(this.value);
    if (unmaskedValue) {
      this.value = this.getMaskedValue(unmaskedValue);
      this.showingSIN = false;
      this.showMaskedSIN = !this.maskWhileTyping;
      if (this.showMaskedSIN) {
        setTimeout(() => {
          this.hiddenSINElement.nativeElement.innerHTML = this.getHiddenSINString();
        });
      }
    } else {
      this.value = '';
    }
  }

  public showEditableSINInput(focusInput: boolean = false): void {
    if (this.disabled || !this.showMaskedSIN) return;

    this.showMaskedSIN = false;
    if (focusInput) {
      setTimeout(() => {
        this.sinInputElement?.nativeElement.focus();
      });
    }
  }

  public toggleShowHide(): void {
    if (this.disabled) return;
    this.showingSIN = !this.showingSIN;
    this.hiddenSINElement.nativeElement.innerHTML = this.getHiddenSINString();
    this.showMaskedSIN = !this.maskWhileTyping && !this.showingSIN && !!this.value;
  }

  public getMaskedDisplayString(): string {
    return this.getHiddenSINString();
  }

  public isSINVisible(): boolean {
    return this.maskWhileTyping ? this.showingSIN : !this.showMaskedSIN;
  }

  private getMaskedValue(inputValue: string): string {
    return maskitoTransform(inputValue, this.maskOptions);
  }

  private getUnmaskedValue(value: string): string {
    return value.replace(/\D/g, '');
  }

  protected getHiddenSINString(): string {
    const input = this.value.replace(/\s/g, '') ?? '';
    if (!input) return '';
    const formattedCharacters: string[] = [];
    for (let i = 0; i < input.length; i++) {
      if (i > 0 && i % 3 === 0) {
        formattedCharacters.push('&nbsp;');
      }
      if (input.length >= 7 && i >= 6) {
        formattedCharacters.push(input[i]);
      } else {
        formattedCharacters.push(this.showingSIN ? input[i] : `<span class="masked-character">\u23FA</span>`);
      }
    }
    return formattedCharacters.join('');
  }
}
